import React, { useState } from 'react';
import { PrimeArtifactState } from '../../types/primeModel';
import { DemandType, DemandFlowType } from '../../types/enums';
import { ArtifactStateTransitionType } from '../../types/enums';

interface PrimeStateFormProps {
  initialData?: Partial<PrimeArtifactState>;
  onSubmit: (state: PrimeArtifactState) => void;
  onBack: () => void;
}

const PrimeStateForm: React.FC<PrimeStateFormProps> = ({ initialData, onSubmit, onBack }) => {
  const [currentState, setCurrentState] = useState<Partial<PrimeArtifactState>>(
    initialData || {
      name: '',
      description: '',
      prime_artifact_ref: '',
      is_required: false,
      state_type: ArtifactStateTransitionType.OPERATIONAL,
      is_initial: false,
      is_final: false,
      is_improvement: false,
      is_resource_active: false,
      demand_type: DemandType.SUPPLY,
      demand_flow_type: DemandFlowType.PULL,
      state_metadata: {},
      state_rules: {},
      state_parameters: {},
      validation_rules: {},
      improvement_ref: null,
      improvement_parameters: {},
      created_at: new Date().toISOString(),
      updated_at: null,
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentState.name) {
      // Ensure we have all required fields with proper enum values
      const stateToSubmit = {
        ...currentState,
        prime_artifact_ref: initialData?.prime_artifact_ref || '',
        created_at: new Date().toISOString(),
        state_type: currentState.state_type || ArtifactStateTransitionType.OPERATIONAL,
        demand_type: currentState.demand_type || DemandType.SUPPLY,
        demand_flow_type: currentState.demand_flow_type || DemandFlowType.PULL,
        is_required: currentState.is_required || false,
        is_initial: currentState.is_initial || false,
        is_final: currentState.is_final || false,
        is_improvement: currentState.is_improvement || false,
        state_metadata: currentState.state_metadata || {},
        state_rules: currentState.state_rules || {},
        state_parameters: currentState.state_parameters || {},
        validation_rules: currentState.validation_rules || {},
        improvement_ref: currentState.improvement_ref || null,
        improvement_parameters: currentState.improvement_parameters || {},
      };
      onSubmit(stateToSubmit as PrimeArtifactState);
    }
  };

  const handleChange = (field: keyof PrimeArtifactState, value: unknown) => {
    setCurrentState((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="prime-state-form">
      <h2>Artifact State</h2>

      <div className="form-group">
        <label htmlFor="state-name">Name</label>
        <input
          type="text"
          id="state-name"
          value={currentState.name || ''}
          onChange={(e) => handleChange('name', e.target.value)}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="state-description">Description</label>
        <textarea
          id="state-description"
          value={currentState.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="state-type">State Type</label>
        <select
          id="state-type"
          value={currentState.state_type || ArtifactStateTransitionType.OPERATIONAL}
          onChange={(e) => handleChange('state_type', e.target.value)}
        >
          <option value={ArtifactStateTransitionType.OPERATIONAL}>Operational</option>
          <option value={ArtifactStateTransitionType.MATERIAL}>Material</option>
          <option value={ArtifactStateTransitionType.RESOURCE}>Resource</option>
          <option value={ArtifactStateTransitionType.CREATION}>Creation</option>
          <option value={ArtifactStateTransitionType.IMPROVEMENT}>Improvement</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="demand-type">Demand Type</label>
        <select
          id="demand-type"
          value={currentState.demand_type || DemandType.SUPPLY}
          onChange={(e) => handleChange('demand_type', e.target.value)}
        >
          <option value={DemandType.SUPPLY}>Supply (Default)</option>
          <option value={DemandType.TARGET}>Target</option>
          <option value={DemandType.CAPACITY}>Capacity</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="demand-flow-type">Demand Flow Type</label>
        <select
          id="demand-flow-type"
          value={currentState.demand_flow_type || DemandFlowType.PULL}
          onChange={(e) => handleChange('demand_flow_type', e.target.value)}
        >
          <option value={DemandFlowType.PULL}>Pull (Default)</option>
          <option value={DemandFlowType.DRUM}>Drum</option>
          <option value={DemandFlowType.PUSH}>Push</option>
        </select>
      </div>

      <div className="form-group checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={currentState.is_required || false}
            onChange={(e) => handleChange('is_required', e.target.checked)}
          />
          Required
        </label>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={currentState.is_initial || false}
            onChange={(e) => handleChange('is_initial', e.target.checked)}
          />
          Initial State
        </label>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={currentState.is_final || false}
            onChange={(e) => handleChange('is_final', e.target.checked)}
          />
          Final State
        </label>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={currentState.is_improvement || false}
            onChange={(e) => handleChange('is_improvement', e.target.checked)}
          />
          Improvement State
        </label>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onBack}>
          Back
        </button>
        <button type="submit">Save State</button>
      </div>
    </form>
  );
};

export default PrimeStateForm;
